// The fields a user picks for a table export. A view keeps a fixed catalog
// of the fields that mean something to a reader, each ticked or not by
// default; the export writes the ones picked, in the catalog's order.

import { ExportCell, ExportColumn, ExportTable } from "./table";

// A field as the picker shows it
export interface ExportFieldOption {
  key: string;
  label: string;
  // The heading it is listed under
  group: string;
  // Ticked until the user says otherwise
  selected: boolean;
  // What stands quieter beside the label: "a column per specification"
  hint?: string;
}

// One column a field writes
export interface ExportFieldColumn<R> extends ExportColumn {
  value: (row: R) => ExportCell;
}

export interface ExportField<R> extends ExportFieldOption {
  // The columns the field writes for these rows: one for most fields; one
  // per key the rows hold for a field that spreads (a column per
  // specification), none when they hold no key
  columns: (rows: readonly R[]) => ExportFieldColumn<R>[];
}

// A field that writes one column, headed by its label unless told otherwise
export const columnField = <R>(
  option: ExportFieldOption,
  column: Omit<ExportFieldColumn<R>, "header"> & { header?: string },
): ExportField<R> => {
  const written = { ...column, header: column.header ?? option.label };
  return { ...option, columns: () => [written] };
};

// The keys of the fields ticked by default
export const defaultFieldKeys = (
  fields: readonly ExportFieldOption[],
): string[] => fields.filter((field) => field.selected).map((field) => field.key);

/**
 * The rows as a table of the fields picked.
 *
 * @param name - The sheet's name
 * @param fields - The view's catalog
 * @param keys - The keys of the fields picked; written in the catalog's
 *   order, whatever the order of the keys
 * @param rows - The rows
 * @returns The table
 */
export const tableOfFields = <R>(
  name: string,
  fields: readonly ExportField<R>[],
  keys: readonly string[],
  rows: readonly R[],
): ExportTable => {
  const picked = new Set(keys);
  const columns = fields
    .filter((field) => picked.has(field.key))
    .flatMap((field) => field.columns(rows));
  return {
    name,
    columns: columns.map(({ header, kind, width }) => ({ header, kind, width })),
    rows: rows.map((row) => columns.map((column) => column.value(row))),
  };
};
